import { Plate } from '@/components/landing/primitives';
import { stagger } from '@/components/landing/stagger';
import { FOUNDER } from '@/content/site';

const NUMBERS = [
  { value: '300+', label: 'organizations served' },
  { value: '14,000+', label: 'community members' },
] as const;

// CPF's mark is a framed square, so it needs more height than the two wordmarks
// to carry the same visual weight.
const CLIENTS = [
  { src: '/img/enterprise-cpf.webp', alt: 'CPF', width: 101, height: 96, size: 'h-12' },
  { src: '/img/enterprise-sony.webp', alt: 'Sony', width: 516, height: 96, size: 'h-9 md:h-10' },
  { src: '/img/enterprise-cdg.webp', alt: 'CDG', width: 335, height: 96, size: 'h-9 md:h-10' },
] as const;

// Universities where AGICAFET has spoken, taught or designed courses (owner,
// 2026-10-04). White marks like the clients; the Thammasat seal and SMU's
// stacked mark are fine line art, so they sit taller than the wordmarks.
const UNIVERSITIES = [
  { src: '/img/edu-chula.webp', alt: 'Chulalongkorn University', width: 341, height: 128, size: 'h-11 md:h-14' },
  { src: '/img/edu-tu.webp', alt: 'Thammasat University', width: 128, height: 128, size: 'h-14 md:h-[4.5rem]' },
  { src: '/img/edu-kmitl.webp', alt: "King Mongkut's Institute of Technology Ladkrabang", width: 286, height: 128, size: 'h-9 md:h-11' },
  { src: '/img/edu-kmutt.webp', alt: "King Mongkut's University of Technology Thonburi", width: 281, height: 128, size: 'h-11 md:h-14' },
  { src: '/img/edu-cmu.webp', alt: 'Chiang Mai University', width: 303, height: 128, size: 'h-9 md:h-11' },
  { src: '/img/edu-ntu-ea.webp', alt: 'Nanyang Technological University Singapore, Entrepreneurship Academy', width: 717, height: 128, size: 'h-8 md:h-10' },
  { src: '/img/edu-smu-iie.webp', alt: 'Singapore Management University, Institute of Innovation & Entrepreneurship', width: 193, height: 128, size: 'h-14 md:h-[4.5rem]' },
] as const;

const BADGES = [
  { src: '/img/nv-badge-agentic.webp', alt: 'NVIDIA-Certified Professional: Agentic AI' },
  { src: '/img/nv-badge-genai.webp', alt: 'NVIDIA-Certified Professional: Generative AI LLMs' },
] as const;

/**
 * Evidence, once, at the size evidence deserves: the numbers at display size,
 * the clients as marks, who leads the work, and one event. The program
 * memberships live in the hero and `#stacks`; they are not repeated here.
 * "Registered training vendor" is the honest label for these clients — they
 * are training clients, and the page never implies they are deployments.
 */
export function ProofSection() {
  return (
    <Plate eyebrow="Proof" id="proof" labelledBy="proof-title">
      <h2 id="proof-title" className="lp-h2 lp-reveal max-w-4xl" style={stagger(1)}>
        Trusted across Thailand
      </h2>
      <p className="lp-body lp-reveal mt-6 max-w-2xl" style={stagger(2)}>
        We help companies, universities and teams put AI to real work.
      </p>

      <dl className="lp-reveal mt-16 flex flex-wrap gap-x-24 gap-y-10 md:mt-20" style={stagger(3)}>
        {NUMBERS.map((n) => (
          <div key={n.label}>
            <dt className="sr-only">{n.label}</dt>
            <dd>
              <span className="t-stat">{n.value}</span>
              <span className="mt-3 block text-[17px] text-muted-foreground" aria-hidden>
                {n.label}
              </span>
            </dd>
          </div>
        ))}
      </dl>

      <div className="lp-reveal mt-16" style={stagger(4)}>
        <p className="text-[15px] text-muted-foreground">Registered training vendor for 20+ organizations, including</p>
        <ul className="mt-6 flex flex-wrap items-center gap-x-14 gap-y-6" aria-label="Clients">
          {CLIENTS.map((c) => (
            <li key={c.alt}>
              <img
                loading="lazy"
                decoding="async"
                src={c.src}
                alt={c.alt}
                width={c.width}
                height={c.height}
                className={`${c.size} w-auto opacity-85`}
              />
            </li>
          ))}
        </ul>
      </div>

      <div className="lp-reveal mt-14" style={stagger(5)}>
        <p className="text-[15px] text-muted-foreground">
          Trusted by universities in Thailand and Singapore to speak, teach and design courses
        </p>
        <ul className="mt-6 flex flex-wrap items-center gap-x-12 gap-y-8" aria-label="Universities">
          {UNIVERSITIES.map((u) => (
            <li key={u.alt}>
              <img
                loading="lazy"
                decoding="async"
                src={u.src}
                alt={u.alt}
                width={u.width}
                height={u.height}
                className={`${u.size} w-auto opacity-85`}
              />
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-20 grid gap-12 border-t border-border pt-16 lg:grid-cols-[5fr_7fr] lg:gap-16">
        <div className="lp-reveal" style={stagger(6)}>
          <div className="flex items-center gap-5">
            {BADGES.map((b) => (
              <img
                loading="lazy"
                decoding="async"
                key={b.src}
                src={b.src}
                alt={b.alt}
                width={160}
                height={167}
                className="h-20 w-auto md:h-24"
              />
            ))}
          </div>
          <p className="t-h3 mt-8">
            {FOUNDER.name} ({FOUNDER.nickname})
          </p>
          <p className="mt-1 text-[15px] text-muted-foreground">{FOUNDER.role}</p>
          <p className="mt-5 text-[17px] leading-relaxed text-[hsl(var(--fg-secondary))]">
            NVIDIA-Certified Professional in Agentic AI and in Generative AI LLMs, and completed NVIDIA’s
            End-to-End Robotics Workflow with Isaac training at GTC Taipei.
          </p>
        </div>

        <figure className="lp-reveal" style={stagger(7)}>
          <img
            loading="lazy"
            decoding="async"
            src="/img/nv-event-group-1200.webp"
            srcSet="/img/nv-event-group-800.webp 800w, /img/nv-event-group-1200.webp 1200w, /img/nv-event-group-1600.webp 1600w"
            sizes="(min-width: 1360px) 710px, (min-width: 1024px) 55vw, 100vw"
            width={1600}
            height={586}
            alt="The audience of the NVIDIA RTX AI PC Event at Thammasat Business School"
            className="w-full rounded-xl"
          />
          <figcaption className="t-caption mt-3">
            NVIDIA RTX AI PC Event at Thammasat Business School, with NVIDIA and SVOA.
          </figcaption>
        </figure>
      </div>
    </Plate>
  );
}
